import {
	bidSuretyBondClosedReasonSchema,
	carrierName,
	currencySchema,
	Dtos,
	finalContractSuretyBondClosedReasonSchema,
	finalContractSuretyBondFormTypeSchema,
	isoDateSchema,
	suretyBondId,
	suretyBondRenewalMethodSchema,
	suretyBondStatusSchema,
	suretyQuoteId,
} from "@inrev/common";
import * as z from "zod";
import { bondFormSchema, obligeeSchema, projectSchema } from "../schemas/obligations";
import { defineTool } from "../tool-definition";

const termSchema = z.strictObject({
	amount: currencySchema,
	premium: currencySchema,
	rate: z.number(),
	effectiveDate: isoDateSchema,
	expirationDate: isoDateSchema.nullable(),
});

const bondBase = {
	bondId: suretyBondId,
	number: z.string(),
	status: suretyBondStatusSchema,
	carrierName,
	penalSum: currencySchema,
	terms: z.array(termSchema),
	project: projectSchema,
	obligees: z.array(obligeeSchema),
	form: bondFormSchema,
	continuous: z.boolean(),
	cancellable: z.boolean(),
	renewalMethod: suretyBondRenewalMethodSchema.nullable(),
	sfaaCode: Dtos.SfaaCode.Get.Response.schema.nullable(),
	closedAt: isoDateSchema.nullable(),
	closedNote: z.string().nullable(),
	bondRequestId: suretyQuoteId.nullable(),
};

export const getBond = defineTool({
	name: "get_bond",
	description:
		"One bond on the account. In a bond conversation it is that bond and `bondId` is ignored; on the account pass `bondId` from `list_bonds`. `type` is `bid` or `final`, and the fields after it differ by type. `penalSum` is the bond's amount: on a bid bond the bid it secures, on a final bond the newest term's amount. `terms` run newest first; a bid bond's term `amount` is its face amount, a share of the bid. A bid bond carries `bidDate`, `bidToFinalRequestId` (the request to turn it into a final) and `finalBondId` (the final bond it became); a closed bid bond's `closedReason` is the bid result (`bid_won`, `bid_lost`, `not_awarded`). A final bond carries `contractAmount` and `contractDate`, expected and actual completion, the actual contract amount at close, and `bidBondId`, the bid bond it came from. `project` is the job: description, city and state, NAICS scope of work, months to complete, warranty, contract hazards and damages. `obligees` name who the bond protects and their role. `form` is the bond form and how it was issued. `sfaaCode` is the SFAA catalog class from the bond, or null when unset. What this bond counts toward exposure is on `derive_open_exposure`, not here.",
	label: "Bond",
	contexts: ["account", "bond"],
	implementation: "api",
	audience: "model",
	input: z.strictObject({
		bondId: suretyBondId.optional(),
	}),
	data: z.discriminatedUnion("type", [
		z.strictObject({
			...bondBase,
			type: z.literal("bid"),
			bidDate: isoDateSchema.nullable(),
			finalBondFormType: finalContractSuretyBondFormTypeSchema.nullable(),
			bidToFinalRequestId: suretyQuoteId.nullable(),
			finalBondId: suretyBondId.nullable(),
			closedReason: bidSuretyBondClosedReasonSchema.nullable(),
		}),
		z.strictObject({
			...bondBase,
			type: z.literal("final"),
			contractAmount: currencySchema,
			contractDate: isoDateSchema.nullable(),
			expectedCompletionDate: isoDateSchema.nullable(),
			actualCompletionDate: isoDateSchema.nullable(),
			actualContractAmount: currencySchema.nullable(),
			bidBondId: suretyBondId.nullable(),
			closedReason: finalContractSuretyBondClosedReasonSchema.nullable(),
		}),
	]),
});
